import type { FiscalError, SaleVerdict } from '@krp/client-runtime'
import {
  COLLAPSIBLE_PAYMENT_STAGES,
  formatKopecks,
  type PaymentFlowVm,
  type PaymentKind,
  type PaymentNoticeVm,
  type PaymentStage,
  type ProgressStepState,
  type ProgressStepVm,
  type ProgressVm,
} from '@krp/sale-ui'

import type { AgentSessionModel } from '../../agent-session/model/agentSessionController'
import { DOUBLE_RECEIPT_TEXT, buildUnresolvedProgress, type UnknownVerdict } from '../../operation/model/progress'
import type { OwnWordsState } from '../../operation/model/words'
import type { OwnPaymentAttempt } from './paymentAttemptSlice'
import type { OwnSaleState } from './saleState'

/**
 * Окно оплаты собственной кассы (RP-PAY-ONE, шаг 7) — полный адаптер к общему `PaymentFlowVm`. Этап считает ЧИСТАЯ функция
 * `ownPaymentStageOf` по продаже окна (этап и итог агента), неразобранной записи агента и попытке; содержимое этапа — `buildOwnPaymentFlow`.
 * Оплата и чек у агента — ОДНА команда, поэтому этапа «Подготовка» нет (окно открывается сразу на терминале или чеке), ручной сверки и
 * позднего слипа нет (у агента свои слова человека под «Подробнее»). Отменить платёж с терминала агент не умеет (автоотмена удалена,
 * К-44): у терминала кнопки «Отменить» нет, причина — под «Подробнее».
 */

/** Причина, почему у терминала нет «Отменить»: кассир ищет кнопку, пока терминал ждёт карту. */
export const CANCEL_UNAVAILABLE_TEXT =
  'Отменить оплату из программы нельзя: кассовый агент не снимает платёж с терминала. Отмените её на самом терминале — агент вернёт отказ, и окно покажет «Не оплачено».'

/** Неразобранная продажа, о которой говорит окно: итог этой продажи, а если её нет — первая не отложенная запись журнала агента. */
export interface ShownUnknown {
  verdict: UnknownVerdict
  amountKopecks: number | null
  /** Когда агент записал операцию (запись журнала после F5); у продажи окна — `null` (время — у попытки). */
  since: string | null
}

export function shownUnknown(state: OwnSaleState, session: AgentSessionModel): ShownUnknown | null {
  const own = state.verdict
  if (state.phase === 'unknown' && own?.kind === 'unknown') return { verdict: own, amountKopecks: state.paidTotalKopecks, since: null }
  if (state.phase !== 'composing' || session.unresolved?.kind !== 'unknown') return null
  const id = session.unresolved.meta?.operationId
  const entry = session.inDoubt.find((item) => item.operationId === id)
  const total = entry?.total ?? null
  return { verdict: session.unresolved, amountKopecks: total === null ? null : Math.round(total * 100), since: entry?.at ?? null }
}

/** Ключ операции у агента для действий над неразобранной записью; `null` — не известен. */
export function unresolvedOperationId(state: OwnSaleState, session: AgentSessionModel): string | null {
  const shown = shownUnknown(state, session)
  return shown?.verdict.meta?.operationId ?? session.pendingUnknown ?? session.inDoubt.find((entry) => !entry.parked)?.operationId ?? null
}

export interface OwnPaymentStageInput {
  state: OwnSaleState
  /** Неразобранная продажа окна или запись журнала агента (`shownUnknown`). */
  unknown: ShownUnknown | null
  /** Попытка оплаты окна; нет — оплату здесь не нажимали (F5). */
  attempt: OwnPaymentAttempt | null
}

/** Неизвестный исход: связь с агентом или исход карты — «Уточняем»; документа нет или касса отказала в чеке — «деньги есть, чека нет». */
function unknownStage(verdict: UnknownVerdict): PaymentStage {
  if (verdict.error?.code === 'agent-unreachable' || verdict.card?.outcome === 'unknown') return 'checking'
  if (verdict.error?.code === 'no-document-found' || verdict.meta?.oneCStage === 'FiscalRefused') return 'no-receipt'
  return 'receipt'
}

/**
 * Этап окна оплаты собственной кассы — ЧИСТАЯ функция, одна таблица переходов. `null` — окна нет.
 * - `paying` — команда у агента: карта — «У терминала», наличные — «Чек»;
 * - `failed` — агент ответил «не пробит наверняка»: по карте ответил терминал — «Не оплачено», иначе отказ до денег — «Оплата не началась»;
 * - `unknown` (или запись журнала после F5) — по виду неизвестного: «Уточняем», «Чек» (исход чека), «Деньги есть, чека нет»;
 * - `finished` — «Готово»;
 * - сборка корзины после нажатия, которое агент не принял (порог закрылся), — «Оплата не началась», пока кассир не закрыл окно.
 */
export function ownPaymentStageOf(input: OwnPaymentStageInput): PaymentStage | null {
  const { state, unknown, attempt } = input
  switch (state.phase) {
    case 'finished':
      return 'done'
    case 'paying':
      return state.paidKind === 'Card' ? 'terminal' : 'receipt'
    case 'failed':
      return state.verdict?.kind === 'not-sold' && state.verdict.card !== null ? 'declined' : 'not-started'
    case 'unknown':
    case 'composing':
      if (unknown !== null) return unknownStage(unknown.verdict)
      return state.phase === 'composing' && attempt !== null && attempt.aborted && !attempt.closed ? 'not-started' : null
  }
}

export interface OwnPaymentFlowInput {
  resourceId: string
  state: OwnSaleState
  session: AgentSessionModel
  words: OwnWordsState
  attempt: OwnPaymentAttempt | null
  /** Новую оплату можно начать сейчас (порог агента, корзина, кассир): «Повторить оплату» и «Наличными» доступны. */
  canPayNow: boolean
  /** Почему нельзя — словами рядом с кнопкой. */
  payBlockedReason: string | null
}

const step = (key: ProgressStepVm['key'], label: string, state: ProgressStepState, text: string): ProgressStepVm => ({ key, label, state, text })

/** Две отметки проведения (RP-FB35 K4): оплата и чек — в каждом этапе окна. */
export function stepsOf(card: boolean, payment: ProgressStepState, paymentText: string, receipt: ProgressStepState, receiptText: string): readonly ProgressStepVm[] {
  return [step('payment', card ? '① Оплата (терминал)' : 'Наличные получены', payment, paymentText), step('receipt', '② Чек (фискальный накопитель)', receipt, receiptText)]
}

type FlowBase = Omit<PaymentFlowVm, 'stage' | 'steps' | 'title' | 'text' | 'tone'>

/** Отказ агента — в окне дословно (ADR-8), код — под «Подробнее» для администратора. */
function noticeOf(error: FiscalError | null, fallback: string): PaymentNoticeVm {
  const notArrived = error?.code === 'not-delivered'
  return {
    tone: notArrived ? 'warning' : 'danger',
    title: notArrived ? 'Команда не дошла' : 'Команда не выполнена',
    text: error?.text ?? fallback,
    hint: error?.action ?? null,
    meta: error === null ? null : `Код: ${error.code}`,
  }
}

/** Слова агента после пробитого чека, которые кассиру надо прочитать: тогда итог ждёт «Понятно», а не закрывается сам. */
function doneWarnings(verdict: SaleVerdict | null): string[] {
  if (verdict?.kind !== 'sold' || verdict.meta === undefined) return []
  const meta = verdict.meta
  const warnings: string[] = []
  if (meta.printed === false) warnings.push(`${meta.notice ?? 'Чек пробит, но не напечатан. Проверьте ленту.'} «Допечатать» — в блоке состояния кассы.`)
  else if (meta.notice !== null) warnings.push(meta.notice)
  if (meta.possibleDoubleReceipt) warnings.push(DOUBLE_RECEIPT_TEXT)
  return warnings
}

const fromProgress = (base: FlowBase, stage: PaymentStage, progress: ProgressVm): PaymentFlowVm => {
  const { phase: _phase, ...content } = progress
  return { ...base, ...content, watchNote: content.watchNote ?? null, stage }
}

/** Окно оплаты собственной кассы для этапа; `null` — окна нет. */
export function buildOwnPaymentFlow(input: OwnPaymentFlowInput): PaymentFlowVm | null {
  const { state, session, words, attempt } = input
  const unknown = shownUnknown(state, session)
  const stage = ownPaymentStageOf({ state, unknown, attempt })
  if (stage === null) return null
  const journalId = state.phase === 'composing' ? (unknown?.verdict.meta?.operationId ?? null) : null
  const key = journalId !== null ? `journal:${journalId}` : (attempt?.id ?? `own:${input.resourceId.toLowerCase()}`)
  const mine = attempt !== null && attempt.id === key ? attempt : null
  const kind: PaymentKind = state.paidKind ?? mine?.kind ?? attempt?.kind ?? state.paymentKind
  const card = kind === 'Card'
  const amountKopecks = state.paidTotalKopecks ?? unknown?.amountKopecks ?? null
  const collapsible = COLLAPSIBLE_PAYMENT_STAGES.has(stage)
  const base: FlowBase = {
    attemptKey: key,
    watchNote: null,
    amountText: amountKopecks === null ? '' : formatKopecks(amountKopecks),
    details: [],
    waiting: false,
    waitingSince: null,
    collapsed: collapsible && mine?.collapsed === true,
    canCollapse: collapsible,
    canCancelPayment: false,
    cancelling: false,
    cancelUnavailable: null,
    manualRequired: false,
    canFiscalize: false,
    canMarkOutside: false,
    retry: null,
    park: null,
    warnings: [],
    tools: [],
    repeat: null,
    alternative: null,
    canClose: false,
    latePayment: null,
    resolve: null,
    done: null,
    notice: null,
  }
  const repeat = (label: string): PaymentFlowVm['repeat'] => ({ kind, label, enabled: input.canPayNow, blockedReason: input.canPayNow ? null : input.payBlockedReason })
  const verdict = state.verdict

  switch (stage) {
    case 'done': {
      const sold = verdict?.kind === 'sold' ? verdict : null
      const number = sold?.fiscal?.fiscalDocumentNumber
      const receiptId = number == null ? null : String(number)
      const text = `${receiptId !== null ? `№ ${receiptId} · ` : ''}${base.amountText}. Можно обслуживать следующего покупателя.`
      const warnings = doneWarnings(verdict)
      return {
        ...base,
        stage,
        steps: stepsOf(card, 'done', card ? 'оплачено' : 'получены', 'done', 'пробит'),
        title: 'Чек пробит',
        text,
        tone: 'success',
        warnings,
        // Галочка на долю секунды и сразу новая продажа; слова агента («не напечатан», «возможно два чека») — с «Понятно».
        done: { receiptId, text, autoClose: warnings.length === 0 },
      }
    }
    case 'terminal':
      return {
        ...base,
        stage,
        steps: stepsOf(true, 'active', 'ждём карту', 'waiting', 'после оплаты'),
        title: 'Оплата картой',
        text: 'Приложите карту к терминалу.\nЖдём ответ терминала, затем касса пробьёт чек.',
        tone: 'neutral',
        waiting: true,
        cancelUnavailable: CANCEL_UNAVAILABLE_TEXT,
      }
    case 'declined': {
      const error = verdict?.kind === 'not-sold' ? verdict.error : null
      const voided = verdict?.kind === 'not-sold' && verdict.card?.voided === true
      const reason = error?.text ?? 'Терминал отказал в оплате.'
      return {
        ...base,
        stage,
        steps: stepsOf(true, 'failed', 'не прошла', 'failed', 'не пробит'),
        title: 'Не оплачено',
        // «Деньги не списаны» — один раз: у ручной отмены это уже сказано.
        text: voided ? `${reason} Платёж по карте отменён вручную, деньги не списаны.` : `${reason}\nДеньги не списаны.`,
        tone: 'danger',
        details: [...(error?.action != null ? [error.action] : []), ...(error !== null ? [`Код: ${error.code}`] : [])],
        repeat: repeat('Повторить оплату'),
        alternative: { kind: 'Cash', label: 'Наличными', enabled: input.canPayNow },
        canClose: true,
      }
    }
    case 'not-started': {
      // Отказ агента до кассы (`failed` без ответа терминала) или команду не отправили вовсе (порог закрылся в момент нажатия).
      const error = state.phase === 'failed' && verdict?.kind === 'not-sold' ? verdict.error : null
      const aborted = state.phase === 'composing'
      const notice = aborted
        ? { ...noticeOf(null, session.notice?.text ?? session.gate.text), hint: session.gate.advice }
        : noticeOf(error, 'Агент не принял чек.')
      return {
        ...base,
        stage,
        steps: card
          ? stepsOf(true, 'failed', 'не началась', 'waiting', 'после оплаты')
          : stepsOf(false, 'waiting', 'не проведены', 'failed', 'не пробит'),
        title: card ? 'Оплата не началась' : 'Чек не пробит',
        text: card ? 'Оплата не начиналась — деньги не списаны, корзина цела.' : 'Касса чек не приняла — корзина цела, повторите, когда причина устранена.',
        tone: 'danger',
        notice,
        repeat: repeat('Повторить'),
        canClose: true,
      }
    }
    case 'receipt':
      if (state.phase === 'paying') {
        return {
          ...base,
          stage,
          steps: stepsOf(false, 'done', 'получены', 'active', 'печатаем'),
          title: 'Печатаем чек',
          text: 'Команда у кассового агента. Ждём результата кассы.',
          tone: 'neutral',
          waiting: true,
        }
      }
      break
    default:
      break
  }

  // Неизвестный исход (продажа окна или запись журнала): содержимое и слова человека — ход проведения собственного ядра.
  if (unknown === null) return null
  const progress = buildUnresolvedProgress({
    verdict: unknown.verdict,
    amountKopecks: unknown.amountKopecks,
    operationId: unresolvedOperationId(state, session),
    words,
    busy: session.busy !== null,
  })
  return fromProgress({ ...base, waitingSince: stage === 'checking' ? (mine?.startedAt ?? unknown.since) : null }, stage, progress)
}
